/**
 * @description User-facing discount error copy (LYN-247). Keep in sync with
 * backend/constants/discountErrors.js. Wave 2/5: FieldError + coupon inline.
 *
 * @dependencies FieldError accepts { message, code }
 * @notes Evaluate is HTTP 200 with codeError / codeErrorCode — do not treat
 *        a code miss as a failed cart request.
 */

export const DISCOUNT_ERRORS = {
  CODE_NOT_FOUND: {
    code: 'CODE_NOT_FOUND',
    field: 'code',
    message: "That discount code isn't valid. Check the spelling and try again.",
  },
  CODE_INACTIVE: {
    code: 'CODE_INACTIVE',
    field: 'code',
    message: 'That discount code is no longer active.',
  },
  CODE_NOT_STARTED: {
    code: 'CODE_NOT_STARTED',
    field: 'code',
    message: "That discount code isn't available yet.",
  },
  CODE_EXPIRED: {
    code: 'CODE_EXPIRED',
    field: 'code',
    message: 'That discount code has expired.',
  },
  CODE_NOT_APPLICABLE: {
    code: 'CODE_NOT_APPLICABLE',
    field: 'code',
    message: "That discount code doesn't apply to this order.",
  },
  CODE_COMBINATION: {
    code: 'CODE_COMBINATION',
    field: 'code',
    message: "This code can't be combined with another discount on this order.",
  },
  CODE_REQUIRED: {
    code: 'CODE_REQUIRED',
    field: 'code',
    message: 'Enter a discount code, or switch this discount to apply automatically.',
  },
  CODE_IN_USE: {
    code: 'CODE_IN_USE',
    field: 'code',
    message: 'This discount code is already in use. Choose a different code, or deactivate the other discount first.',
  },
  TITLE_REQUIRED: {
    code: 'TITLE_REQUIRED',
    field: 'title',
    message: 'Enter a name for this discount.',
  },
  METHOD_REQUIRED: {
    code: 'METHOD_REQUIRED',
    field: 'method',
    message: 'Choose whether this discount applies automatically or with a code.',
  },
  VALUE_REQUIRED: {
    code: 'VALUE_REQUIRED',
    field: 'value',
    message: 'Enter a discount amount.',
  },
  PERCENTAGE_RANGE: {
    code: 'PERCENTAGE_RANGE',
    field: 'value',
    message: 'Percent off must be between 0 and 100.',
  },
  NEGATIVE_AMOUNT: {
    code: 'NEGATIVE_AMOUNT',
    field: 'value',
    message: 'Amount off cannot be negative.',
  },
  DATE_RANGE: {
    code: 'DATE_RANGE',
    field: 'endsAt',
    message: 'The end date must be on or after the start date.',
  },
  INVALID_SUBTOTAL: {
    code: 'INVALID_SUBTOTAL',
    field: 'subtotalCents',
    message: 'Enter a valid order subtotal.',
  },
  TARGET_PRODUCT: {
    code: 'TARGET_PRODUCT',
    field: 'targets',
    message: 'Choose a product that belongs to this company.',
  },
  TARGET_VARIANT: {
    code: 'TARGET_VARIANT',
    field: 'targets',
    message: 'That variant does not belong to the selected product.',
  },
  TARGETS_REQUIRED: {
    code: 'TARGETS_REQUIRED',
    field: 'targets',
    message: 'Choose at least one product. These products count toward R2.',
  },
  TARGETS_VARIANT_REQUIRED: {
    code: 'TARGETS_VARIANT_REQUIRED',
    field: 'targets',
    message: 'Choose at least one variant. These products count toward R2.',
  },
  NOT_FOUND: {
    code: 'DISCOUNT_NOT_FOUND',
    field: null,
    message: 'This discount is no longer available.',
  },
  VALIDATION: {
    code: 'DISCOUNT_VALIDATION',
    field: null,
    message: 'Check the highlighted fields and try again.',
  },
  EVALUATE_UNAVAILABLE: {
    code: 'EVALUATE_UNAVAILABLE',
    field: 'code',
    message: "Couldn't check discounts right now. Save still re-checks on the server.",
  },
};

/**
 * Map evaluate output to a FieldError prop. Null when the code applied or none typed.
 * @param {{ codeError?: string|null, codeErrorCode?: string|null }} result
 * @returns {{ message: string, code: string, field: string }|null}
 */
export function discountCodeFieldError(result) {
  if (!result || !result.codeError) {
    return null;
  }
  const known = DISCOUNT_ERRORS[result.codeErrorCode];
  return {
    message: result.codeError,
    code: result.codeErrorCode || 'CODE_NOT_FOUND',
    field: known?.field || 'code',
  };
}

/**
 * Map a CRUD 400/409 axios error to FieldError props keyed by field.
 * Banner copy still comes from getApiErrorMessage.
 * @param {unknown} error
 * @returns {Record<string, { message: string, code: string }>}
 */
export function discountApiFieldErrors(error) {
  const data = error?.response?.data;
  if (!data) {
    return {};
  }
  const entries = Array.isArray(data.errors) && data.errors.length > 0
    ? data.errors
    : (data.error && typeof data.error === 'object' ? [data.error] : []);
  const out = {};
  for (const entry of entries) {
    if (entry && entry.field && typeof entry.message === 'string' && entry.message) {
      out[entry.field] = {
        message: entry.message,
        code: entry.code || 'DISCOUNT_VALIDATION',
      };
    }
  }
  return out;
}

export default DISCOUNT_ERRORS;
